import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Button,
  Fade,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Article as ArticleIcon,
  QuestionAnswer as QuestionAnswerIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import { AssessedSession } from "../../../redux/actions/assessmentActions";
import { FormSectionOutput } from "../../../redux/types/formTypes";
import {
  formatAnswer,
  isJsonAnswer,
  hasAnswerContent,
} from "./assessmentHelpers";
import JsonSchemaAnswerRenderer from "./JsonSchemaAnswerRenderer";

interface AssessedSessionFormViewProps {
  currentAssessedSession: AssessedSession;
}

const AssessedSessionFormView: React.FC<AssessedSessionFormViewProps> = ({
  currentAssessedSession,
}) => {
  const formAnalysis = currentAssessedSession.form_analysis;
  console.log(formAnalysis);
  const [copiedDebug, setCopiedDebug] = useState(false);
  const [tocOpen, setTocOpen] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );
  const [showScrollButton, setShowScrollButton] = useState(false);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const contentsRef = useRef<HTMLDivElement | null>(null);

  // Initialize all sections as expanded
  useEffect(() => {
    if (formAnalysis?.form_outputs) {
      const allSectionIds = new Set(
        formAnalysis.form_outputs.map(
          (section: FormSectionOutput) => section.section_id
        )
      );
      setExpandedSections(allSectionIds);
    }
  }, [formAnalysis]);

  // Track scroll to show/hide button after passing Contents section
  useEffect(() => {
    const handleScroll = () => {
      if (contentsRef.current) {
        const contentsBottom =
          contentsRef.current.getBoundingClientRect().bottom;
        setShowScrollButton(contentsBottom < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Scroll to top handler
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Detect Ctrl+F and expand all sections
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Detect Ctrl+F (Windows/Linux) or Cmd+F (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === "f") {
        // Expand all sections when Ctrl+F is pressed
        if (formAnalysis?.form_outputs) {
          const allSectionIds = new Set(
            formAnalysis.form_outputs.map(
              (section: FormSectionOutput) => section.section_id
            )
          );
          setExpandedSections(allSectionIds);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [formAnalysis]);

  // Handle copy debug JSON
  const handleCopyDebugJson = () => {
    navigator.clipboard.writeText(JSON.stringify(formAnalysis, null, 2));
    setCopiedDebug(true);
    setTimeout(() => setCopiedDebug(false), 2000);
  };

  // Handle section navigation from TOC
  const handleSectionClick = (sectionId: string) => {
    // Expand the section if it's collapsed
    if (!expandedSections.has(sectionId)) {
      setExpandedSections(
        new Set(Array.from(expandedSections).concat(sectionId))
      );
    }

    // Wait a bit for the accordion to expand, then scroll
    setTimeout(() => {
      const element = sectionRefs.current[sectionId];
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  // Handle accordion change
  const handleAccordionChange =
    (sectionId: string) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      const newExpanded = new Set(Array.from(expandedSections));
      if (isExpanded) {
        newExpanded.add(sectionId);
      } else {
        newExpanded.delete(sectionId);
      }
      setExpandedSections(newExpanded);
    };

  return (
    <Box sx={{ display: "flex", gap: 3, position: "relative" }}>
      {/* Table of Contents - Sticky Sidebar */}
      {currentAssessedSession.state === "ready" &&
        formAnalysis?.form_outputs &&
        formAnalysis.form_outputs.length > 0 && (
          <Box
            ref={contentsRef}
            sx={{
              width: tocOpen ? 280 : 48,
              flexShrink: 0,
              position: "sticky",
              top: 16,
              height: "fit-content",
              maxHeight: "calc(100vh - 120px)",
              transition: "width 0.3s ease",
              zIndex: 10,
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: tocOpen ? 2 : 1,
                bgcolor: "background.paper",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={tocOpen ? 2 : 0}
              >
                {tocOpen && (
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    color="primary"
                  >
                    Contents
                  </Typography>
                )}
                <IconButton
                  size="small"
                  onClick={() => setTocOpen(!tocOpen)}
                  sx={{ ml: tocOpen ? 0 : 0 }}
                >
                  {tocOpen ? (
                    <CloseIcon fontSize="small" />
                  ) : (
                    <MenuIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>

              {tocOpen && (
                <List
                  dense
                  sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}
                >
                  {formAnalysis.form_outputs.map(
                    (sectionOutput: FormSectionOutput, index: number) => {
                      const sectionSchema =
                        formAnalysis.form_output_schema?.sections[
                          sectionOutput.section_id
                        ];
                      if (!sectionSchema) return null;

                      const answeredCount = sectionOutput.answers.filter((q) =>
                        hasAnswerContent(q.llm_answer)
                      ).length;
                      const totalCount = sectionOutput.answers.length;

                      return (
                        <ListItem key={sectionOutput.section_id} disablePadding>
                          <ListItemButton
                            onClick={() =>
                              handleSectionClick(sectionOutput.section_id)
                            }
                            sx={{
                              borderRadius: 1,
                              mb: 0.5,
                              "&:hover": {
                                bgcolor: "primary.50",
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <ArticleIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight="medium">
                                  {sectionSchema.section_title}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {answeredCount}/{totalCount} answered
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    }
                  )}
                </List>
              )}
            </Paper>
          </Box>
        )}

      {/* Main Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Grid container spacing={3}>
          {/* Form Analysis Results */}
          {currentAssessedSession.state === "ready" && formAnalysis ? (
            <>
              {formAnalysis.form_output_schema &&
              formAnalysis.form_outputs &&
              formAnalysis.form_outputs.length > 0 ? (
                <>
                  {formAnalysis.form_outputs.map(
                    (
                      sectionOutput: FormSectionOutput,
                      sectionIndex: number
                    ) => {
                      const sectionSchema =
                        formAnalysis.form_output_schema?.sections[
                          sectionOutput.section_id
                        ];

                      if (!sectionSchema) return null;

                      return (
                        <Grid item xs={12} key={sectionOutput.section_id}>
                          <div
                            ref={(el) =>
                              (sectionRefs.current[sectionOutput.section_id] =
                                el)
                            }
                          >
                            <Accordion
                              expanded={expandedSections.has(
                                sectionOutput.section_id
                              )}
                              onChange={handleAccordionChange(
                                sectionOutput.section_id
                              )}
                              sx={{ mb: 2 }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                  bgcolor: "primary.50",
                                  "&:hover": { bgcolor: "primary.100" },
                                }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  flexWrap="wrap"
                                >
                                  <ArticleIcon color="primary" />
                                  <Typography variant="h6" fontWeight="medium">
                                    {sectionSchema.section_title}
                                  </Typography>
                                  <Chip
                                    label={`Section ${sectionIndex + 1}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                  {sectionOutput.source_inference_id && (
                                    <Tooltip title="Click to copy">
                                      <Chip
                                        label={`Inference: ${sectionOutput.source_inference_id}`}
                                        size="small"
                                        color="default"
                                        variant="outlined"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (
                                            sectionOutput.source_inference_id
                                          ) {
                                            navigator.clipboard.writeText(
                                              sectionOutput.source_inference_id
                                            );
                                          }
                                        }}
                                        sx={{
                                          fontFamily: "monospace",
                                          fontSize: "0.7rem",
                                          cursor: "pointer",
                                          "&:hover": {
                                            bgcolor: "action.hover",
                                          },
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails
                                sx={{
                                  // Keep content in DOM but visually hidden for Ctrl+F to work
                                  visibility: expandedSections.has(
                                    sectionOutput.section_id
                                  )
                                    ? "visible"
                                    : "hidden",
                                  height: expandedSections.has(
                                    sectionOutput.section_id
                                  )
                                    ? "auto"
                                    : 0,
                                  overflow: expandedSections.has(
                                    sectionOutput.section_id
                                  )
                                    ? "visible"
                                    : "hidden",
                                  padding: expandedSections.has(
                                    sectionOutput.section_id
                                  )
                                    ? undefined
                                    : 0,
                                }}
                              >
                                <Box>
                                  {sectionSchema.section_description && (
                                    <Box mb={2}>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontStyle: "italic" }}
                                      >
                                        {sectionSchema.section_description}
                                      </Typography>
                                      <Divider sx={{ mt: 2 }} />
                                    </Box>
                                  )}

                                  {/* Questions and Answers */}
                                  {sectionOutput.answers.map(
                                    (questionOutput, qIndex) => {
                                      const questionSchema =
                                        sectionSchema.questions[
                                          questionOutput.question_id
                                        ];

                                      if (!questionSchema) return null;

                                      const hasAnswer = hasAnswerContent(
                                        questionOutput.llm_answer
                                      );

                                      return (
                                        <Box
                                          key={questionOutput.question_id}
                                          mb={3}
                                        >
                                          {/* Question Header */}
                                          <Box
                                            display="flex"
                                            alignItems="flex-start"
                                            gap={1.5}
                                            mb={1.5}
                                          >
                                            <QuestionAnswerIcon
                                              fontSize="small"
                                              color="action"
                                              sx={{ mt: 0.5 }}
                                            />
                                            <Box flex={1}>
                                              <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                mb={0.5}
                                              >
                                                <Typography
                                                  variant="subtitle2"
                                                  fontWeight="medium"
                                                  color="text.secondary"
                                                >
                                                  Question {qIndex + 1}
                                                </Typography>
                                                {hasAnswer ? (
                                                  <CheckCircleIcon
                                                    sx={{
                                                      fontSize: 20,
                                                      color: "success.main",
                                                    }}
                                                    titleAccess="Answered"
                                                  />
                                                ) : (
                                                  <CancelIcon
                                                    sx={{
                                                      fontSize: 20,
                                                      color: "error.main",
                                                    }}
                                                    titleAccess="Not Answered"
                                                  />
                                                )}
                                              </Box>
                                              {/* Question Text */}
                                              <Typography
                                                variant="body1"
                                                fontWeight="medium"
                                                sx={{ mb: 1.5 }}
                                              >
                                                {questionSchema.text}
                                              </Typography>
                                            </Box>
                                          </Box>

                                          {/* Answer */}
                                          <Box
                                            sx={{
                                              pl: 4,
                                              py: 1.5,
                                              pr: 2,
                                              bgcolor: hasAnswer
                                                ? "grey.50"
                                                : "transparent",
                                              borderRadius: 1,
                                              borderLeft: 3,
                                              borderColor: hasAnswer
                                                ? "success.main"
                                                : "grey.300",
                                            }}
                                          >
                                            {isJsonAnswer(
                                              questionOutput.llm_answer
                                            ) &&
                                            questionSchema.answer_json_schema ? (
                                              <JsonSchemaAnswerRenderer
                                                schema={
                                                  questionSchema.answer_json_schema
                                                }
                                                formData={
                                                  questionOutput.llm_answer
                                                }
                                                showAnswerStatus={true}
                                              />
                                            ) : (
                                              <Typography
                                                variant="body2"
                                                color={
                                                  hasAnswer
                                                    ? "text.primary"
                                                    : "text.secondary"
                                                }
                                                sx={{
                                                  lineHeight: 1.6,
                                                  whiteSpace: "pre-wrap",
                                                  fontStyle: hasAnswer
                                                    ? "normal"
                                                    : "italic",
                                                }}
                                              >
                                                {formatAnswer(
                                                  questionOutput.llm_answer
                                                )}
                                              </Typography>
                                            )}
                                          </Box>

                                          {qIndex <
                                            sectionOutput.answers.length -
                                              1 && <Divider sx={{ mt: 3 }} />}
                                        </Box>
                                      );
                                    }
                                  )}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          </div>
                        </Grid>
                      );
                    }
                  )}

                  {/* Raw Form Analysis (for debugging) */}
                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                          pr={1}
                        >
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Raw Form Analysis (Debug)
                          </Typography>
                          <Tooltip
                            title={copiedDebug ? "Copied!" : "Copy JSON"}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyDebugJson();
                              }}
                              sx={{ ml: 1 }}
                            >
                              {copiedDebug ? (
                                <CheckCircleIcon
                                  sx={{ fontSize: 18 }}
                                  color="success"
                                />
                              ) : (
                                <ContentCopyIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre style={{ overflow: "auto", fontSize: "0.75rem" }}>
                          {JSON.stringify(formAnalysis, null, 2)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Form analysis data is incomplete or missing schema
                    information.
                  </Alert>
                </Grid>
              )}
            </>
          ) : (
            <Grid item xs={12}>
              <Alert severity="info">
                {currentAssessedSession.state === "processing"
                  ? "This form assessment session is currently being processed. Please check back later for results."
                  : currentAssessedSession.state === "active"
                  ? "This form assessment session is currently active."
                  : currentAssessedSession.state === "created"
                  ? "This form assessment session has been created but not yet started."
                  : "No form analysis results available for this session."}
              </Alert>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Back to Contents Button */}
      <Fade in={showScrollButton}>
        <Button
          color="primary"
          variant="contained"
          size="medium"
          onClick={handleScrollToTop}
          sx={{
            position: "fixed",
            bottom: 24,
            left: 250,
            transition: "left 0.3s ease",
            zIndex: 1000,
          }}
        >
          <Tooltip title="Back to Contents" placement="right">
            <>
              Back to Contents
              <KeyboardArrowUpIcon />
            </>
          </Tooltip>
        </Button>
      </Fade>
    </Box>
  );
};

export default AssessedSessionFormView;
